import React, { useEffect, useState } from "react";
import { useCustomerAccount } from "../SignInSecurity/AccountProvider";
import { formatPhoneNumber2, getTMXSessionID } from "../../utils";
import { getErrorCode, handleApiError } from "../../utils/errors";
import { getNewPhoneOTP, verifyNewPhoneOTP } from "../../api/custAccountApi";
import analytics from "../../utils/analytics";
import {
  Modal,
  ModalHeader,
  ModalBody
} from "@one-thd/sui-atomic-components";
import {
  OtpOptionsView,
  OtpEmailView,
  OtpPhoneSmsView,
  OtpLoadingView,
  OtpPhoneEvoiceView,
  OtpInfoView,
  OtpTextLinkOptions,
  OtpTermsAndConditionsView,
} from "./OtpModalViews";

import {
  callOtpValidate,
  callOtpRequest
} from "./ApiUtils";
import { updateEmailForWebAuthnCredentials } from "../../utils/webauthnUtils";
import { ApiContext } from "../../contexts/ApiContext";
import { ErrorBoundary } from "@thd-olt-component-react/error-boundary";

/**
 * @param isShown               toggle model display
 * @param emailEditContext      set of fields used to determine a user's flow
 * @param onVerifyOtpSuccess    callback function used when it receive a successful OTP response
 * @param onVerifyOtpFailure    callback function used when it receive a non-2xx OTP response
 * @param onCancelModal         callback function used when user cancels modal
 */

/**
 * New modal flow allowing users with validated phone numbers
 *  multiple ways to validate their OTP
 * @param NewVerifyOtpModalProps otpModalProps required props passed from parent
 */
const NewVerifyOtpModal = (props) => {
  const {
    origin,
    emailEditContext = {},
    onUnverifiedPhoneChangeSuccess = () => { },
    onVerifiedPhoneChangeSuccess = () => { },
    activateAlertMessage,
    isOpen,
    resetFormState,
    onCancelModal,
    currentEmail,
    onVerificationSuccess,
    onVerifyOtpSuccess,
    verifyExistingPhone,
    errorOnVerifyingNumber,
  } = props;

  const { apiContext, setApiContext } = React.useContext(ApiContext)
  const { autoToken } = apiContext;

  const {
    email,
    phone,
    otpChannel,
    newEmail,
    isPhoneVerified,
    newPhoneNumber = "" // only available with phone change flow
  } = emailEditContext;

  const context = useCustomerAccount();
  const { setStale, account } = context;

  const [curModalView, setCurModalView] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [errorState, setErrorState] = useState([]);
  const [isLandlineSMS, setIsLandlineSMS] = useState(false);

  const [isModalLoading, setIsModalLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Please wait");

  // Helper function to display loading modal with a message
  const startLoading = (loadMessage) => {
    setLoadingMessage(loadMessage);
    setIsModalLoading(true);
  }
  // Helper function to navigate to another modal view
  const navigateToModal = (modalName) => {
    setErrorState([]);
    setCurModalView(modalName);
    setIsModalLoading(false);
  }

  // Change success message depending on flow and phone verification
  const displaySuccessMessage = () => {
    if (origin === "phoneEdit") {
      return isPhoneVerified ? onVerifiedPhoneChangeSuccess() : onUnverifiedPhoneChangeSuccess();
    }
    if (origin === "verifyExistingPhone") {
      return onVerificationSuccess(true);
    }
    return () => { };
  };

  const handleVerifyOtpErrors = (error, pageSection) => {
    const errorCode = getErrorCode(error);
    const errorMessage = handleApiError(error, pageSection);
    //  if AUTH_ERR_125, reset screen and display error message for exceeding OTP attempts
    if (errorCode === "AUTH_ERR_125") {
      setIsModalLoading(false);
      resetFormState();
      activateAlertMessage(errorCode);
    } else if (errorCode === "CCA_ERR_002") {
      setIsModalLoading(false);
      setIsLandlineSMS(true);
    } else {
      setErrorState([errorMessage]);
      setIsModalLoading(false);
    }
  }

  /**
     * Initial flow when component first gets rendered
     */
  useEffect(() => {
    if (origin === "emailEdit") {
      if (otpChannel === "sms") {
        // Displays options modal if user has a verified phone number
        setCurModalView("options");
        setShowModal(true);
      } else if (otpChannel === "email") {
        // If phone, present all options to the user.
        setCurModalView("options");
        setShowModal(true);
      }
    } else if (origin === "phoneEdit") {
      // Show options modal if phone number is verified
      if (isPhoneVerified) {
        setCurModalView("options");
      } else {
        // Send OTP to existing email address
        setCurModalView("info");
      }
      setShowModal(true);
    } else if (origin === "verifyExistingPhone") {
      navigateToModal("newPhoneVerificationSms");
      setShowModal(true);
    } else if ((origin === "termsAndConditions")) {
      navigateToModal("newPhoneVerificationTerms");
      setShowModal(true);
    }
  }, []);

  /**
     * Helper function for new phone OTP verification flow
     * @param {number} phoneNumber
     * @param {string} message
     * @returns
     */
  const sendNewPhoneOtp = (phoneNumber, message, channel) => {
    const { requestOtpTicket } = apiContext;
    const userId = origin === "emailEdit" ? null : requestOtpTicket?.userId;

    // Apply additional fields into the request body when email edit flow is being called
    let additionalFields = {};
    if (origin === "emailEdit") {
      additionalFields = {
        updateEmailForUnverifiedPhone: true,
        newLogonId: newEmail,
        emailLastModifiedDate: account?.profile?.lastModifiedDate
      };
    }

    startLoading(message);

    return getNewPhoneOTP(
      account?.profile?.emailId,
      phoneNumber,
      channel,
      userId,
      getTMXSessionID(),
      account.encryptVerifiedPhoneToken
    ).then(({ response, error }) => {
      if (response) {
        // Successfully sent code to new number, going to OTP verification step
        setApiContext({
          ...(response.data?.autoToken && { autoToken: response.data.autoToken }),
          requestOtpTicket: {
            ...apiContext.requestOtpTicket,
            backendHostName: response.data?.backendHostName,
            userId: response.data?.userId,
            authId: response.data?.authId,
            ...additionalFields
          }
        });
        const newModal = channel === "sms" ? "newPhoneVerificationSms" : "newPhoneVerificationEvoice";
        navigateToModal(newModal);
      } else if (error) {
        handleVerifyOtpErrors(error, "secure your account - get otp");
      }
    });
  };

  const fireOverlayAnalyticsEvent = (otpChannel, origin) => {
    let deliveryMethod;
    switch (otpChannel) {
      case "sms":
        deliveryMethod = "text";
        break;
      case "phone":
        deliveryMethod = "phone call";
        break;
      default:
        deliveryMethod = otpChannel;
    }
    let flow = "verify my phone number";
    if (origin === "emailEdit") {
      flow = "email address edit"
    }
    analytics.postOverlayEvent(`${flow} code entry - ${deliveryMethod}`);
  }

  /**
     * Callback function when user submits their OTP choice
     * @param {string} otpChoice
     */
  const onClickOptions = (otpChoice, origin) => {
    let loadMessage = "";
    let flowName = "";
    if (otpChoice === "email") {
      loadMessage = `We're emailing a code to: ${email}`;
      flowName = "emailOtpRequest";
    }
    else if (otpChoice === "sms") {
      loadMessage = `We're texting a code to ${formatPhoneNumber2(phone)}...`;
      flowName = "smsOtpRequest";
    } else if (otpChoice === "phone") {
      loadMessage = `We're calling ${formatPhoneNumber2(phone)} with a code...`;
      flowName = "evoiceOtpRequest";
    }

    startLoading(loadMessage);
    const emailOtp = newEmail || email;
    callOtpRequest({
      flowName,
      currentEmail,
      emailOtp,
      account,
      setApiContext,
      autoToken
    }).then((response) => {
      if (response.data?.isLandlineSMS && otpChoice === "sms") {
        setIsLandlineSMS(true);
        setIsModalLoading(false);
      } else {
        setApiContext({
          ...(response.data?.autoToken && { autoToken: response.data.autoToken }),
          requestOtpTicket: {
            ...apiContext.requestOtpTicket,
            backendHostName: response.data?.backendHostName,
            userId: response.data?.userId,
            authId: response.data?.authId,
          }
        });

        navigateToModal(otpChoice);
        fireOverlayAnalyticsEvent(otpChoice, origin);
      }

    }).catch((error) => {
      handleVerifyOtpErrors(error, "secure your account - get otp");
      
    });
  };

  /**
     * Callback function when user submits their OTP choice while verifying new phone
     * @param {string} otpChoice
     */
  const onClickNewPhoneOptions = (otpChoice) => {
    const displayPhone = origin === "emailEdit" ? emailEditContext.phone : newPhoneNumber

    let loadMessage = `We're calling ${formatPhoneNumber2(displayPhone)} with a code...`
    if (otpChoice === "sms") {
      loadMessage = `Sending a code to your new number: ${formatPhoneNumber2(displayPhone)}`
      if (origin === "emailEdit") {
        loadMessage = `We're texting a code to ${formatPhoneNumber2(displayPhone)}`
      }
    }

    sendNewPhoneOtp(displayPhone, loadMessage, otpChoice);
    fireOverlayAnalyticsEvent(otpChoice, origin);
  };

  /**
     * Validates otp sent from SMS during new phone verification
     * @param {string} otpCode code sent from OTP request
     */
  const verifyNewPhoneWithOtp = (otpCode) => {
    startLoading("We're verifying your identity");

    const displayPhone = origin === "emailEdit" ? emailEditContext.phone : newPhoneNumber

    verifyNewPhoneOTP(otpCode, apiContext.requestOtpTicket, displayPhone, account, autoToken).then(({ response, error }) => {
      if (response) {
        if (origin === "phoneEdit") {
          analytics.postSettingsSubmissionEvent("security:phone edit success");
        }
        if (origin === "verifyExistingPhone") {
          analytics.postSettingsSubmissionEvent("security:phone verification success");
        }
        // Possible origins: emailEdit, phoneEdit, verifyExistingPhone
        if (origin === "emailEdit") {
          emailEditContext.origin = "emailEdit"
          analytics.postSettingsSubmissionEvent("security:email address edit success");
          onVerifyOtpSuccess(emailEditContext);
        } else {
          setIsModalLoading(false);
          resetFormState();
          setStale();
          displaySuccessMessage();
        }
      } else if (error) {
        handleVerifyOtpErrors(error, "verify phone");
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  /**
     * Callback used to verify OTP sent through any channel
     * @param {string} otpCode
     * @param {string} channel
     */
  const verifyOtpCallback = (otpCode) => {
    const loadingText = ["phone", "sms"].includes(curModalView) && origin !== "emailEdit" ? "We're verifying your phone number..." : "We're verifying your identity";
    startLoading(loadingText);

    const otpValidateEmail = newEmail || email;
    callOtpValidate(otpCode, otpValidateEmail, apiContext, account, setApiContext).then(() => {
      if (origin === "phoneEdit") {
        // let user choose b/w text or phone number to verify new phone number
        navigateToModal("newPhoneVerifyOptions");
      } else if (origin === "emailEdit") {
        // If a user successfully updates his email, his WebAuthn credentials should too.
        const { profile } = account;
        updateEmailForWebAuthnCredentials(otpValidateEmail, profile.emailId);
        // Email updated successfully after validating OTP
        analytics.postSettingsSubmissionEvent("security:email address edit success");
        onVerifyOtpSuccess(emailEditContext);
      }
    }).catch((error) => {

      handleVerifyOtpErrors(error, "verify phone");
    });
  };

  /**
     * Helper method used to render appropriate modal
     * @returns {JSX.Element} A modal view based on current flow
     */
  const renderView = () => {
    // Unmounts any existing component when loading indicator is on
    if (isModalLoading) {
      return <OtpLoadingView
        setCurModalView={setCurModalView}
        loadingMessage={loadingMessage}
        {...props} />;
    }
    if (curModalView === "info") {
      return <OtpInfoView
        enablePhoneVerifyButton={origin === "emailEdit" && !isPhoneVerified}
        onClickCantAccess={() => navigateToModal("newPhoneVerifyOptions")}
        submitButtonAction={() => onClickOptions("email", origin)}
        cancelButtonAction={onCancelModal}
        errorState={errorState}
        currentView={curModalView}
        {...props}
      />
    }
    if (curModalView === "options") {
      return <OtpOptionsView
        onClickCallback={onClickOptions}
        setCurModalView={setCurModalView}
        isPhoneVerified={isPhoneVerified}
        errorState={errorState}
        currentView={curModalView}
        isLandlineSMS={isLandlineSMS}
        {...props} />;
    }
    if (curModalView === "email") {
      return <OtpEmailView
        setCurModalView={setCurModalView}
        onSubmitCallback={verifyOtpCallback}
        onClickOptions={onClickOptions}
        errorState={errorState}
        isPhoneVerified={isPhoneVerified}
        currentView={curModalView}
        OtpTextLinkOptionsComponent={OtpTextLinkOptions}
        {...props} />;
    }
    if (curModalView === "sms") {
      return <OtpPhoneSmsView
        setCurModalView={setCurModalView}
        onSubmitCallback={verifyOtpCallback}
        onClickOptions={onClickOptions}
        otpPhoneNumber={phone}
        isPhoneVerified={isPhoneVerified}
        errorState={errorState}
        currentView={curModalView}
        OtpTextLinkOptionsComponent={OtpTextLinkOptions}
        {...props} />;
    }
    // E-Voice option
    if (curModalView === "phone") {
      return <OtpPhoneEvoiceView
        setCurModalView={setCurModalView}
        onSubmitCallback={verifyOtpCallback}
        onClickOptions={onClickOptions}
        otpPhoneNumber={phone}
        isPhoneVerified={isPhoneVerified}
        errorState={errorState}
        currentView={curModalView}
        OtpTextLinkOptionsComponent={OtpTextLinkOptions}
        {...props} />;
    }
    if (curModalView === "newPhoneVerifyOptions") {
      return <OtpOptionsView
        onClickCallback={onClickNewPhoneOptions}
        setCurModalView={setCurModalView}
        onClickOptions={onClickOptions}
        isPhoneVerified={isPhoneVerified}
        errorState={errorState}
        isValidateNewPhone={origin !== "emailEdit"}
        currentView={curModalView}
        isLandlineSMS={isLandlineSMS}
        {...props} />;
    }
    if (curModalView === "newPhoneVerificationTerms") {
      analytics.postSettingsSubmissionEvent("security: verification phone link");
      return <OtpTermsAndConditionsView
        verifyExistingPhone={verifyExistingPhone}
        setCurModalView={setCurModalView}
        phone={formatPhoneNumber2(phone)}
        onCancelModal={onCancelModal} />;
    }
    /**
         * Flows used to verify new phone number once an initial validation is
         *  performed on their existing email or phone number
         */
    if (curModalView === "newPhoneVerificationSms") {
      return <OtpPhoneSmsView
        setCurModalView={setCurModalView}
        onSubmitCallback={verifyNewPhoneWithOtp}
        onClickOptions={onClickOptions}
        otpPhoneNumber={origin === "emailEdit" ? emailEditContext.phone : newPhoneNumber}
        errorState={errorState}
        isVerifyingANewPhone
        isPhoneVerified={isPhoneVerified}
        currentView={curModalView}
        OtpTextLinkOptionsComponent={OtpTextLinkOptions}
        navigateToModal={navigateToModal}
        {...props} />;
    }
    if (curModalView === "newPhoneVerificationEvoice") {
      return <OtpPhoneEvoiceView
        setCurModalView={setCurModalView}
        onSubmitCallback={verifyNewPhoneWithOtp}
        onClickOptions={onClickOptions}
        otpPhoneNumber={origin === "emailEdit" ? emailEditContext.phone : newPhoneNumber}
        errorState={errorState}
        isPhoneVerified={isPhoneVerified}
        isVerifyingANewPhone
        currentView={curModalView}
        OtpTextLinkOptionsComponent={OtpTextLinkOptions}
        navigateToModal={navigateToModal}
        {...props} />;
    }
    return <></>;
  };

  const renderHeader = () => {
    const updatingPhone = [
      "newPhoneVerifyOptions",
      "newPhoneVerificationSms",
      "newPhoneVerificationEvoice",
      "newPhoneVerificationTerms",
    ]
    let phrase = "Verify Your Identity";

    if (updatingPhone.includes(curModalView) && (origin === "phoneEdit")) {
      phrase = "Verify Your New Phone Number"
    }

    if (updatingPhone.includes(curModalView) && (origin === "verifyExistingPhone" || origin === "termsAndConditions")) {
      phrase = "Verify Phone Number"
    }

    return phrase;
  }

  React.useEffect(() => {
    if (errorOnVerifyingNumber) setErrorState([errorOnVerifyingNumber]);
  }, [errorOnVerifyingNumber])

  return (
    <ErrorBoundary>
      <Modal
        open={showModal && isOpen}
        onClose={onCancelModal}>
        <ModalHeader>
          {renderHeader()}
        </ModalHeader>
        <ModalBody>
          {renderView()}
        </ModalBody>
      </Modal>
    </ErrorBoundary>
  )
}

export default NewVerifyOtpModal;